import { Button, Form, Input, Modal } from 'semantic-ui-react'
import { Controller, useForm } from 'react-hook-form'
import { useSWRConfig } from 'swr'
import { AxiosError } from 'axios'
import * as React from 'react'

import { useToasts } from './toasts/ToastsProvider'
import { useApi } from '../store/mainContext'

type FormValues = { name: string }

type Dialog = { type: 'name-form' }

interface Props {
  askForName?: boolean
  disabled?: boolean
  text: string
}

const defaultValues: FormValues = { name: 'New Template' }

export default function SaveAsTemplateBtn(props: Props) {
  const { askForName, disabled, text } = props

  const { mutate: globalMutate } = useSWRConfig()
  const { addToast } = useToasts()
  const api = useApi()

  const { reset, ...form } = useForm<FormValues>({ defaultValues })

  const [currentDialog, setCurrentDialog] = React.useState<Dialog | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)

  const handleAddTemplate = async ({ name }: FormValues) => {
    setIsSaving(true)
    return api
      .post('note-templates', {
        name,
        description: '',
        body: text,
        status: 'Draft',
      })
      .then(() => globalMutate(['note-templates']))
      .then(() => setCurrentDialog(null))
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .finally(() => setIsSaving(false))
  }

  const isDisabled = disabled || isSaving

  React.useEffect(() => {
    if (currentDialog?.type === 'name-form') reset()
  }, [currentDialog?.type, reset])
  const inputRef = React.useRef<Input>(null)

  React.useEffect(() => {
    if (inputRef.current && currentDialog?.type === 'name-form') {
      inputRef.current.select() // Selecciona el texto dentro del input
    }
  }, [currentDialog?.type]) // Se ejecuta solo una vez cuando el componente se monta

  return (
    <>
      <Button
        content="Save as Template"
        type="button"
        disabled={isDisabled}
        loading={isSaving}
        basic
        onClick={() =>
          askForName
            ? setCurrentDialog({ type: 'name-form' })
            : handleAddTemplate(defaultValues)
        }
      />
      <Modal
        onClose={() => setCurrentDialog(null)}
        open={currentDialog?.type === 'name-form'}
        closeOnDimmerClick={!isSaving}
        closeOnEscape={!isSaving}
        size="mini"
      >
        <Modal.Header>Save as Template</Modal.Header>
        <Modal.Content className="!pt-0">
          <Form
            onSubmit={form.handleSubmit(handleAddTemplate)}
            spellCheck="false"
            id="save-as-template-form"
          >
            <Controller
              name="name"
              control={form.control}
              rules={{ required: 'Required' }}
              render={({ field, fieldState }) => {
                return (
                  <Form.Field>
                    <label className="text-sm font-semibold leading-none">
                      Name
                    </label>
                    <Input
                      {...field}
                      ref={inputRef}
                      onChange={(_, { value }) => field.onChange(value)}
                      fluid
                      error={!!fieldState.error}
                      disabled={isSaving}
                      input={<input data-1p-ignore autoFocus />}
                    />
                    <div className="text-base leading-normal text-red-600">
                      {fieldState.error?.message}
                    </div>
                  </Form.Field>
                )
              }}
            />
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <Button
            content="Cancel"
            type="button"
            onClick={() => setCurrentDialog(null)}
            disabled={isSaving}
            basic
          />
          <Button
            loading={isSaving}
            disabled={isSaving}
            content="Save"
            primary
            type="submit"
            form="save-as-template-form"
          />
        </Modal.Actions>
      </Modal>
    </>
  )
}
